import React, { useState, useEffect } from "react";
import { UserService, Contact, resolveRole } from "../services/userService";
import { Session } from "../auth/session";
import { RemoteLogger } from "../services/remoteLogger";
import { TemplateService } from "../services/templateService";
import { Header } from "./Header";
import { IoArrowBackOutline, IoAttachOutline } from "react-icons/io5";

const formatBytes = (bytes: number, decimals = 2) => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
};

interface SelectVerifierProps {
  emailSummary: {
    fromOrTo: string;
    subject: string;
    attachments?: { id: string; name: string; size: number }[];
  };
  onSelect: (verifierIds: number[], customQuestion?: string, organizationId?: number) => void;
  onBack: () => void;
  templateId?: number;
  onLogout?: () => void;
  inputFieldValues?: { inputFieldId: number; value: string }[];
}

const SelectVerifier: React.FC<SelectVerifierProps> = ({
  emailSummary,
  onSelect,
  onBack,
  templateId,
  onLogout,
  inputFieldValues,
}) => {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);

  // Selected verifiers (one or more) and the confirm step with the custom question
  const [selectedContacts, setSelectedContacts] = useState<Contact[]>([]);
  const [confirming, setConfirming] = useState(false);
  const [customQuestion, setCustomQuestion] = useState("");

  const isSelected = (id: number) => selectedContacts.some((c) => c.id === id);
  const toggleContact = (contact: Contact) =>
    setSelectedContacts((prev) =>
      prev.some((c) => c.id === contact.id) ? prev.filter((c) => c.id !== contact.id) : [...prev, contact]
    );
  const removeContact = (id: number) => {
    const next = selectedContacts.filter((c) => c.id !== id);
    setSelectedContacts(next);
    if (next.length === 0) setConfirming(false);
  };

  // Invitation drawer states
  const [showInviteDrawer, setShowInviteDrawer] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviting, setInviting] = useState(false);
  const [inviteSuccess, setInviteSuccess] = useState(false);

  // Organization states
  const [memberships, setMemberships] = useState<any[]>([]);
  const [selectedOrgId, setSelectedOrgId] = useState<number | string | null>("personal");
  const [resolvedTemplateId, setResolvedTemplateId] = useState<number | null>(templateId || null);
  const [eligibleOrg, setEligibleOrg] = useState<{ id: number; name: string } | null>(null);

  // Initialization Effect: Load memberships & templateId if not provided
  useEffect(() => {
    const initData = async () => {
      setLoading(true);
      try {
        const token = await Session.getToken();
        if (token) {
          // Fetch memberships
          const membershipRes = await UserService.getMemberships(token);
          let firstOrgId: number | null = null;
          if (membershipRes && membershipRes.success && Array.isArray(membershipRes.data)) {
            setMemberships(membershipRes.data);
            if (membershipRes.data.length > 0) {
              const firstOrg = membershipRes.data[0].organization;
              if (firstOrg && firstOrg.id) {
                firstOrgId = firstOrg.id;
                // Keep initial selectedOrgId as "personal" by default
              }
            }
          }

          // Resolve template ID if not passed as a prop
          let tId = templateId;
          if (!tId) {
            const templateRes = await TemplateService.listAll(token);
            if (templateRes?.data?.data?.templateId) {
              tId = templateRes.data.data.templateId;
            }
          }
          setResolvedTemplateId(tId || null);

          // Fallback fetch if either template ID or org ID is missing
          if (!tId || !firstOrgId) {
            const response = await UserService.listAll(token);
            setContacts(response?.data || []);
          }
        } else {
          // Fallback if not authenticated
          const { fallbackContacts } = require("../services/userService");
          setContacts(fallbackContacts || []);
        }
      } catch (err) {
        RemoteLogger.error("Failed to load initial data for verifier selection:", err);
      } finally {
        setLoading(false);
      }
    };

    initData();
  }, [templateId]);

  // Fetch contacts whenever selectedOrgId, resolvedTemplateId, or inputFieldValues changes
  useEffect(() => {
    const fetchNetworkContacts = async () => {
      if (!resolvedTemplateId) return;
      setLoading(true);
      try {
        const token = await Session.getToken();
        if (token) {
          // 1. Fetch all network contacts
          const orgId =
            selectedOrgId === "personal" || !selectedOrgId ? undefined : Number(selectedOrgId);
          const response = await UserService.listFromNetwork(token, resolvedTemplateId, orgId);
          const fetchedContacts = response?.data || [];

          RemoteLogger.info("remote", fetchedContacts);

          // 2. If inputFieldValues are provided, filter using analyzeRequiredApprovers
          if (inputFieldValues && inputFieldValues.length > 0) {
            try {
              const analyzeRes = await UserService.analyzeRequiredApprovers(
                token,
                resolvedTemplateId,
                inputFieldValues
              );

              if (analyzeRes && analyzeRes.data) {
                const resData = analyzeRes.data.data || analyzeRes.data;

                // Handle eligible organization returned by the API
                if (resData.organization && resData.organization.id) {
                  const apiOrgId = Number(resData.organization.id);
                  if (selectedOrgId !== apiOrgId) {
                    setSelectedOrgId(apiOrgId);
                    setEligibleOrg({ id: apiOrgId, name: resData.organization.name });
                    return; // Return early as changing selectedOrgId will trigger a re-fetch
                  }
                  if (!eligibleOrg || eligibleOrg.id !== apiOrgId) {
                    setEligibleOrg({ id: apiOrgId, name: resData.organization.name });
                  }
                }

                const eligibleIds = new Set<number>();
                const eligibleContacts: Contact[] = [];

                // 1. Direct parsing for arrays of users/ids if they exist
                const rawItems = Array.isArray(resData)
                  ? resData
                  : Array.isArray(resData.data)
                    ? resData.data
                    : Array.isArray(resData.results)
                      ? resData.results
                      : Array.isArray(resData.approvers)
                        ? resData.approvers
                        : [];

                rawItems.forEach((item: any) => {
                  if (typeof item === "number") {
                    eligibleIds.add(item);
                  } else if (item && typeof item === "object") {
                    const userObj = item.user || item;
                    const id = Number(userObj.id || userObj.userId);
                    if (!isNaN(id)) {
                      eligibleIds.add(id);
                      if (userObj.name || userObj.firstName || userObj.email) {
                        eligibleContacts.push({
                          id,
                          name:
                            userObj.name ||
                            (userObj.firstName
                              ? `${userObj.firstName} ${userObj.lastName || ""}`.trim()
                              : userObj.username || userObj.email || "Unknown User"),
                          role: resolveRole(item),
                          roleId: typeof userObj.role === "number" ? userObj.role : undefined,
                          email: userObj.email,
                          avatar: userObj.avatar,
                        });
                      }
                    }
                  }
                });

                // 2. Extract role and group criteria
                const roleIds = Array.isArray(resData.roleIds) ? resData.roleIds.map(Number) : [];
                const approverGroups = Array.isArray(resData.approverGroups)
                  ? resData.approverGroups
                  : [];

                const hasRoleCriteria = roleIds.length > 0;
                const hasGroupCriteria = approverGroups.length > 0;
                const hasDirectIdCriteria = eligibleIds.size > 0;

                let filtered = fetchedContacts;

                if (hasRoleCriteria || hasGroupCriteria || hasDirectIdCriteria) {
                  const eligibleGroupIds = new Set<number>(
                    approverGroups
                      .map((g: any) => Number(g.id || g))
                      .filter((id: number) => !isNaN(id))
                  );

                  filtered = fetchedContacts.filter((c) => {
                    // Direct ID match
                    if (hasDirectIdCriteria && eligibleIds.has(c.id)) {
                      return true;
                    }

                    // Role match
                    if (hasRoleCriteria) {
                      const contactRole = typeof c.roleId === "number" ? c.roleId : Number(c.role);
                      if (!isNaN(contactRole) && roleIds.includes(contactRole)) {
                        return true;
                      }
                    }

                    // Group match
                    if (hasGroupCriteria && eligibleGroupIds.size > 0) {
                      if (
                        Array.isArray(c.groupIds) &&
                        c.groupIds.some((id: any) => eligibleGroupIds.has(Number(id)))
                      ) {
                        return true;
                      }
                      if (
                        Array.isArray(c.groups) &&
                        c.groups.some((g: any) => eligibleGroupIds.has(Number(g.id || g)))
                      ) {
                        return true;
                      }
                      if (Array.isArray(c.memberships)) {
                        for (const m of c.memberships) {
                          if (m) {
                            if (m.groupId && eligibleGroupIds.has(Number(m.groupId))) return true;
                            if (
                              m.groupIds &&
                              Array.isArray(m.groupIds) &&
                              m.groupIds.some((id: any) => eligibleGroupIds.has(Number(id)))
                            )
                              return true;
                            if (
                              m.groups &&
                              Array.isArray(m.groups) &&
                              m.groups.some((g: any) => eligibleGroupIds.has(Number(g.id || g)))
                            )
                              return true;
                            if (m.group && eligibleGroupIds.has(Number(m.group.id || m.group)))
                              return true;
                          }
                        }
                      }
                    }

                    return false;
                  });

                  // Merge direct eligible contacts parsed from response if not already present
                  eligibleContacts.forEach((ec) => {
                    if (!filtered.some((c) => c.id === ec.id)) {
                      filtered.push(ec);
                    }
                  });
                }

                setContacts(filtered);
              } else {
                RemoteLogger.warn(
                  "analyze-required-approvers response was empty or invalid",
                  analyzeRes
                );
                setContacts(fetchedContacts);
              }
            } catch (err) {
              RemoteLogger.error(
                "Failed to filter contacts using analyze-required-approvers:",
                err
              );
              setContacts(fetchedContacts);
            }
          } else {
            setContacts(fetchedContacts);
          }
        }
      } catch (err) {
        RemoteLogger.error("Failed to load contacts from network:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchNetworkContacts();
  }, [resolvedTemplateId, selectedOrgId, JSON.stringify(inputFieldValues)]);

  // A different organization means a different people list
  useEffect(() => {
    setSelectedContacts([]);
    setConfirming(false);
  }, [selectedOrgId]);

  // Filter contacts by search query
  const filteredContacts = contacts.filter((c) => {
    const query = searchQuery.toLowerCase();
    return (
      c.name.toLowerCase().includes(query) ||
      c.role.toLowerCase().includes(query) ||
      (c.email && c.email.toLowerCase().includes(query))
    );
  });

  // Get dynamic gradient classes for initials based on name to make UI vibrant and custom
  const getGradient = (name: string) => {
    const charCodeSum = name.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0);
    const gradients = [
      "from-[#33ccbf] to-[#47ded1]",
      "from-[#3b82f6] to-[#60a5fa]",
      "from-[#8b5cf6] to-[#a78bfa]",
      "from-[#ec4899] to-[#f472b6]",
      "from-[#f59e0b] to-[#fbbf24]",
    ];
    return gradients[charCodeSum % gradients.length];
  };

  const getInitials = (name: string) => {
    const parts = name.split(" ");
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  };

  const handleSendInvite = (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteEmail) return;

    setInviting(true);
    // Simulate API call for invitation
    setTimeout(() => {
      setInviting(false);
      setInviteSuccess(true);
      setInviteEmail("");
      setTimeout(() => {
        setInviteSuccess(false);
        setShowInviteDrawer(false);
      }, 2500);
    }, 1200);
  };

  // Switch between searching contacts and entering custom request details
  if (confirming && selectedContacts.length > 0) {
    return (
      <div className="flex flex-col h-full bg-white animate-fade-in">
        {/* Header */}

        <Header title="Send to gettru app" />
        <button
          aria-label="Back"
          className="text-gray-500 hover:text-gray-700 my-5 px-4"
          onClick={() => setConfirming(false)}
        >
          <IoArrowBackOutline size={16} />
        </button>

        <main className="flex-1 overflow-y-auto px-4 pb-4 flex flex-col justify-between">
          <div className="flex flex-col gap-4">
            {/* Selected Verifiers */}
            <div>
              <span className="block text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1.5">
                {selectedContacts.length === 1 ? "Verifier" : `Verifiers (${selectedContacts.length})`}
              </span>
              <div className="flex flex-col gap-2">
                {selectedContacts.map((contact) => (
                  <div
                    key={contact.id}
                    className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-100 rounded-xl"
                  >
                    {contact.avatar ? (
                      <img
                        src={contact.avatar}
                        alt={contact.name}
                        className="w-10 h-10 rounded-full object-cover border border-gray-200 flex-shrink-0"
                      />
                    ) : (
                      <div
                        className={`w-10 h-10 rounded-full bg-gradient-to-tr ${getGradient(contact.name)} flex items-center justify-center text-white text-xs font-bold shadow-inner flex-shrink-0`}
                      >
                        {getInitials(contact.name)}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <h3 className="text-xs font-bold text-gray-800 truncate">{contact.name}</h3>
                      {contact.email && (
                        <p className="text-[10px] text-gray-500 font-medium truncate mt-0.5">{contact.email}</p>
                      )}
                    </div>
                    <button
                      type="button"
                      aria-label={`Remove ${contact.name}`}
                      title="Remove"
                      onClick={() => removeContact(contact.id)}
                      className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-200/60 rounded-full transition-colors flex-shrink-0"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Email to verify Section */}
            <div>
              <div className="bg-[#FA9B30]/10 border border-[#FA9B30]/50 rounded-xl p-3.5 flex flex-col gap-2">
                <span className="block text-[10px]  uppercase tracking-wider text-[#FA9B30] ">
                  Email to verify
                </span>
                <span className="block text-xs font-bold text-gray-700">
                  {emailSummary.fromOrTo}
                </span>
                <span className="block text-xs font-medium text-gray-500 leading-relaxed">
                  {emailSummary.subject}
                </span>
                {emailSummary.attachments && emailSummary.attachments.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-[#FA9B30]/30">
                    <span className="block text-[9px] font-bold uppercase tracking-wider text-[#FA9B30] mb-1">
                      Attachments ({emailSummary.attachments.length})
                    </span>
                    <div className="flex flex-col gap-1.5">
                      {emailSummary.attachments.map((att) => (
                        <div
                          key={att.id}
                          className="flex items-center gap-1 text-[11px] text-gray-600 truncate"
                        >
                          <IoAttachOutline className="text-gray-400 shrink-0" size={14} />
                          <span className="font-semibold truncate flex-1">{att.name}</span>
                          <span className="text-[10px] text-gray-400 font-medium shrink-0">
                            ({formatBytes(att.size)})
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Custom Question input */}
            <div>
              <label
                htmlFor="custom-question-input"
                className="block text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1.5"
              >
                What do you need verified?
              </label>
              <textarea
                id="custom-question-input"
                rows={3}
                placeholder="e.g., Can you confirm this is genuine and these bank details are correct?"
                value={customQuestion}
                onChange={(e) => setCustomQuestion(e.target.value)}
                className="w-full px-3 py-2.5 text-xs text-gray-700 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:outline-none focus:border-[#33ccbf] focus:ring-2 focus:ring-[#e5f0fb] transition-all duration-200 resize-none leading-relaxed"
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="mt-6 flex gap-3">
            <button
              onClick={() => setConfirming(false)}
              className="flex-1 py-3 text-xs font-bold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-all duration-200 active:scale-[0.98]"
            >
              Back
            </button>
            <button
              onClick={() =>
                onSelect(
                  selectedContacts.map((c) => c.id),
                  customQuestion,
                  selectedOrgId === "personal" || !selectedOrgId ? undefined : Number(selectedOrgId)
                )
              }
              className="flex-1 py-3 text-xs font-bold text-white bg-[#33ccbf] hover:bg-[#44E3D6] rounded-xl shadow-md transition-all duration-200 active:scale-[0.98] transform"
            >
              {selectedContacts.length > 1 ? `Send to ${selectedContacts.length} people` : "Send to App"}
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-white animate-fade-in">
      {/* Header */}
      <Header title="Select Verifier" />

      <button
        aria-label="Back"
        className="text-gray-500 hover:text-gray-700 mt-5 px-4"
        onClick={() => onBack()}
      >
        <IoArrowBackOutline size={16} />
      </button>

      <main className="flex-1 overflow-y-auto px-4 py-4 flex flex-col justify-between">
        <div>
          <p className="text-xs text-gray-500 mb-4 leading-normal">
            Choose one or more people with the gettru app to verify this email on their phone.
          </p>

          {/* Organization Dropdown */}
          {(memberships.length > 0 || eligibleOrg) && (
            <div className="mb-4">
              <label
                htmlFor="org-select"
                className="block text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1.5"
              >
                Organization
              </label>
              <div className="relative">
                <select
                  id="org-select"
                  value={selectedOrgId || "personal"}
                  onChange={(e) =>
                    setSelectedOrgId(
                      e.target.value === "personal" ? "personal" : Number(e.target.value)
                    )
                  }
                  disabled={!!eligibleOrg}
                  className="w-full pl-3 pr-10 py-2.5 text-xs text-gray-700 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:outline-none focus:border-[#33ccbf] focus:ring-2 focus:ring-[#e5f0fb] appearance-none transition-all duration-200 font-semibold disabled:opacity-80 disabled:cursor-not-allowed"
                >
                  {eligibleOrg ? (
                    <option value={eligibleOrg.id}>{eligibleOrg.name}</option>
                  ) : (
                    <>
                      <option value="personal">Personal</option>
                      {memberships.map((m) => (
                        <option key={m.organization.id} value={m.organization.id}>
                          {m.organization.name}
                        </option>
                      ))}
                    </>
                  )}
                </select>
                {!eligibleOrg && (
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3.5 text-gray-400">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2.5"
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Search Box */}
          <div className="relative mb-5 group">
            <input
              type="text"
              placeholder="Search contacts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 text-xs text-gray-700 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:outline-none focus:border-[#33ccbf] focus:ring-2 focus:ring-[#e5f0fb] transition-all duration-200"
            />
            <div className="absolute left-3.5 top-3 text-gray-400 group-focus-within:text-[#33ccbf] transition-colors duration-200">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
            {searchQuery && (
              <button
                type="button"
                aria-label="Clear search"
                title="Clear search"
                onClick={() => setSearchQuery("")}
                className="absolute right-3 top-3 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            )}
          </div>

          {/* Contacts List */}
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12 gap-3">
              <div className="w-6 h-6 border-2 border-gray-200 border-t-[#33ccbf] rounded-full animate-spin" />
              <p className="text-xs text-gray-400">Loading contacts...</p>
            </div>
          ) : filteredContacts.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-xs text-gray-400">No contacts match your search.</p>
            </div>
          ) : (
            <div className="flex flex-col gap-2.5 max-h-[500px] overflow-y-auto pr-1">
              {filteredContacts.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  role="checkbox"
                  aria-checked={isSelected(c.id)}
                  onClick={() => toggleContact(c)}
                  className={`w-full flex items-center gap-3 p-3 border rounded-xl active:scale-[0.98] transform transition-all duration-200 text-left group ${
                    isSelected(c.id)
                      ? "border-[#33ccbf] bg-[#33ccbf]/10"
                      : "bg-white border-gray-100 hover:border-[#33ccbf] hover:bg-[#33ccbf]/5"
                  }`}
                >
                  {/* Dynamic Gradient Initials Avatar */}
                  {c.avatar ? (
                    <img
                      src={c.avatar}
                      alt={c.name}
                      className="w-10 h-10 rounded-full object-cover border border-gray-100 flex-shrink-0"
                    />
                  ) : (
                    <div
                      className={`w-10 h-10 rounded-full bg-gradient-to-tr ${getGradient(c.name)} flex items-center justify-center text-white text-xs font-bold shadow-inner flex-shrink-0`}
                    >
                      {getInitials(c.name)}
                    </div>
                  )}

                  <div className="flex-1 min-w-0">
                    <h3 className="text-xs font-bold text-gray-800  transition-colors duration-150 truncate">
                      {c.name}
                    </h3>

                    {c.email && (
                      <p className="text-[10px] text-gray-500 font-medium truncate mt-0.5">{c.email}</p>
                    )}

                  </div>

                  {/* Selection check */}
                  <div
                    className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all duration-200 ${
                      isSelected(c.id)
                        ? "bg-[#33ccbf] border-[#33ccbf]"
                        : "border-gray-300 group-hover:border-[#33ccbf]"
                    }`}
                  >
                    {isSelected(c.id) && (
                      <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
        {/* Invite Someone Section */}
        <div className="mt-6 border-t border-gray-100 pt-4">
          {!showInviteDrawer ? (
            <button
              onClick={() => setShowInviteDrawer(true)}
              className="w-full flex items-center justify-between p-3.5 bg-[#e5f0fb]/40 hover:bg-[#e5f0fb]/70 border border-[#33ccbf]/20 rounded-xl transition-all duration-200 group active:scale-[0.99]"
            >
              <div className="flex items-center gap-2.5">
                <div className="w-7 h-7 rounded-lg bg-[#33ccbf]/10 flex items-center justify-center text-[#33ccbf]">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
                    />
                  </svg>
                </div>
                <div className="text-left">
                  <span className="block text-xs font-bold text-gray-800 group-hover:text-[#33ccbf] transition-colors">
                    Invite someone to gettru
                  </span>
                  <span className="block text-[9px] text-gray-500 font-medium">
                    Send an invite to join your network
                  </span>
                </div>
              </div>
              <svg
                className="w-3.5 h-3.5 text-gray-400 group-hover:text-[#33ccbf] transition-colors"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 4v16m8-8H4"
                />
              </svg>
            </button>
          ) : (
            <div className="bg-[#e5f0fb]/30 border border-[#33ccbf]/20 rounded-xl p-3.5 animate-slide-up">
              <div className="flex justify-between items-center mb-3">
                <div>
                  <span className="block text-xs font-bold text-gray-800">
                    Invite someone to gettru
                  </span>
                  <span className="block text-[9px] text-gray-500">
                    Send an invite to join your network
                  </span>
                </div>
                <button
                  type="button"
                  aria-label="Close invite drawer"
                  title="Close"
                  onClick={() => {
                    setShowInviteDrawer(false);
                    setInviteEmail("");
                  }}
                  className="p-1 hover:bg-gray-200/50 rounded-full transition-colors"
                >
                  <svg
                    className="w-3.5 h-3.5 text-gray-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              {inviteSuccess ? (
                <div className="py-2.5 text-center text-xs font-bold text-[#33ccbf] bg-white rounded-lg border border-[#33ccbf]/20 animate-pulse">
                  ✨ Invitation sent successfully!
                </div>
              ) : (
                <form onSubmit={handleSendInvite} className="flex gap-2">
                  <input
                    type="email"
                    required
                    placeholder="Enter email address..."
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    className="flex-1 px-3 py-1.5 text-xs text-gray-700 bg-white border border-gray-200 rounded-lg focus:outline-none focus:border-[#33ccbf]"
                  />
                  <button
                    type="submit"
                    disabled={inviting}
                    className="px-3 py-1.5 text-xs font-bold text-white bg-[#33ccbf] rounded-lg hover:bg-[#44E3D6] transition-all disabled:opacity-55 active:scale-95 flex items-center justify-center min-w-[70px]"
                  >
                    {inviting ? "Sending..." : "Send Invite"}
                  </button>
                </form>
              )}
            </div>
          )}
        </div>
      </main>

      {/* Selection bar: pinned to the bottom of the pane, since the whole pane scrolls */}
      {selectedContacts.length > 0 && (
        <div className="sticky bottom-0 z-10 border-t border-gray-100 bg-white px-4 py-3 flex items-center gap-3 shadow-[0_-4px_12px_rgba(0,0,0,0.06)]">
          <div className="flex-1 min-w-0">
            <span className="block text-xs font-bold text-gray-800">{selectedContacts.length} selected</span>
            <button
              type="button"
              onClick={() => setSelectedContacts([])}
              className="text-[10px] font-semibold text-gray-400 hover:text-gray-600 transition-colors"
            >
              Clear
            </button>
          </div>
          <button
            type="button"
            onClick={() => setConfirming(true)}
            className="px-5 py-2.5 text-xs font-bold text-white bg-[#33ccbf] hover:bg-[#2bbdb1] rounded-xl shadow-md transition-all duration-200 active:scale-[0.98]"
          >
            Continue
          </button>
        </div>
      )}
    </div>
  );
};

export default SelectVerifier;
